import React from 'react';
import { ProjectItem } from '../../utils/projectsData';
import { useTranslation } from 'react-i18next';

type ProjectsGridProps = {
  items: ProjectItem[];
};

const toneClassMap: Record<ProjectItem['tags'][number]['tone'], string> = {
  blue: 'bg-blue-50 text-blue-600',
  orange: 'bg-orange-50 text-orange-600',
  teal: 'bg-teal-50 text-teal-600',
  yellow: 'bg-yellow-50 text-yellow-600',
  green: 'bg-green-50 text-green-600',
  gray: 'bg-[#F1F3F5] text-[#94A3B8]'
};

const ProjectsGrid: React.FC<ProjectsGridProps> = ({ items }) => {
  const { t } = useTranslation();

  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item) => (
            <article
              key={item.id}
              className="bg-[#F7F8FA] rounded-xl overflow-hidden border border-[#E2E8F0] hover:shadow-xl hover:border-teal-200 transition-all duration-500 group"
            >
              <div className="h-52 overflow-hidden">
                <img
                  src={item.image}
                  alt={t(`projectsSection.items.${item.id}.title`, { defaultValue: item.title })}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  {item.tags.map((tag, tagIndex) => (
                    <span
                      key={`${item.id}-${tagIndex}`}
                      className={`text-[10px] px-2.5 py-1 rounded-full ${
                        tag.tone === 'gray' ? 'font-medium' : 'font-bold'
                      } ${toneClassMap[tag.tone]}`}
                    >
                      {t(`projectsSection.items.${item.id}.tags.${tagIndex}`, { defaultValue: tag.label })}
                    </span>
                  ))}
                </div>
                <h3 className="font-bold text-[#0F172A] text-base">
                  {t(`projectsSection.items.${item.id}.title`, { defaultValue: item.title })}
                </h3>
                <p className="text-[#64748B] text-sm mt-2 leading-relaxed">
                  {t(`projectsSection.items.${item.id}.description`, { defaultValue: item.description })}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsGrid;
